import React, { useMemo, useState } from "react";
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, TextInput, Platform } from "react-native";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { calculateSpeed, calculatePoints, calculateHandicap } from "../lib/calculations";
import { PublicNav } from "../../components/PublicNav";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";

// AKC Fast CAT title thresholds, mirroring src/lib/calculations.ts.
const TITLES: Array<{ name: string; threshold: number }> = [
  { name: "BCAT", threshold: 150 },
  { name: "DCAT", threshold: 500 },
  { name: "FCAT", threshold: 1000 },
  { name: "FCAT2", threshold: 1500 },
  { name: "FCAT3", threshold: 2000 },
  { name: "FCAT4", threshold: 2500 },
  { name: "FCAT5", threshold: 3000 },
];
function nextTitleFor(points: number): { name: string; threshold: number } | null {
  for (const t of TITLES) if (points < t.threshold) return t;
  // Beyond FCAT5 → each +500 = FCAT(N+1)
  const N = Math.floor((points - 1000) / 500) + 1;
  return { name: `FCAT${N + 1}`, threshold: 1000 + (N + 1) * 500 };
}

// Fast CAT = 100 yards. mph = 100/sec * 3600/1760. time = same constant / mph.
// Helpers used to be module-level arrow consts, then function declarations, but
// kept hitting a "Cannot access 'H' before initialization" TDZ in the prod
// bundle when called from inside useMemo. So now the math is inlined everywhere
// it's needed — slightly more verbose, but immune to bundler hoisting weirdness.

export default function ToolsScreen() {
  const router = useRouter();

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {Platform.OS === "web" && (
        <Head>
          <title>Fast CAT Calculators — Dash-Hound</title>
          <meta name="description" content="Free Fast CAT calculators: convert run time to MPH and points, see how many runs you need to your next title, and figure out what time you need on a third run to hit a target average." />
          <meta property="og:title" content="Fast CAT Calculators — Dash-Hound" />
          <meta property="og:description" content="Three free Fast CAT calculators — points & MPH, title progress, and target run #3 time." />
          <meta property="og:url" content="https://dashhound.app/tools" />
          <meta name="twitter:title" content="Fast CAT Calculators — Dash-Hound" />
          <meta name="twitter:description" content="Three free Fast CAT calculators — points & MPH, title progress, and target run #3 time." />
        </Head>
      )}

      <PublicNav />

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <Text style={styles.heroTitle}>Fast CAT Calculators</Text>
          <Text style={styles.heroSubtitle}>Free tools for points, titles, and target times</Text>
        </View>

        <View style={styles.body}>
          <CalcBoundary><PointsAndMphCalculator /></CalcBoundary>
          <CalcBoundary><TitleProgressCalculator /></CalcBoundary>
          <CalcBoundary><Run3TargetCalculator /></CalcBoundary>

          <Text style={styles.foot}>
            Numbers from these calculators are estimates — actual title points are awarded based on official AKC times. Run the app to track your dog's real history.
          </Text>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text>
        </View>
      </ScrollView>
    </View>
  );
}

// Defensive error boundary so a math/render glitch in one calculator can't
// take down the whole page. Caught error is surfaced as a small inline notice.
class CalcBoundary extends React.Component<{ children: React.ReactNode }, { err: string | null; stack: string | null }> {
  state = { err: null as string | null, stack: null as string | null };
  static getDerivedStateFromError(e: any) { return { err: e?.message || "Something went wrong", stack: e?.stack || null }; }
  componentDidCatch(e: any) { if (typeof console !== "undefined") console.error("[tools]", e); }
  render() {
    if (this.state.err) {
      return (
        <View style={[styles.card, { borderColor: "#FCA5A5", backgroundColor: "#FEF2F2" }]}>
          <Text style={{ color: "#B91C1C", fontWeight: "700" }}>Calculator hit an error</Text>
          <Text style={{ color: "#7F1D1D", fontSize: 13, marginTop: 6 }}>{this.state.err}</Text>
          {this.state.stack ? (
            <Text style={{ color: "#7F1D1D", fontSize: 11, marginTop: 8, fontFamily: Platform.OS === "web" ? "monospace" : undefined }}>
              {String(this.state.stack).split("\n").slice(0, 4).join("\n")}
            </Text>
          ) : null}
          <TouchableOpacity onPress={() => this.setState({ err: null, stack: null })} style={{ marginTop: 12 }}>
            <Text style={{ color: NAVY, fontWeight: "700" }}>Reset →</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return this.props.children;
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Tool 1: Points & MPH
// Inputs: time (s), dog height (in) → outputs MPH + handicap + points
// ──────────────────────────────────────────────────────────────────────────────
function PointsAndMphCalculator() {
  const [time, setTime] = useState("");
  const [height, setHeight] = useState("");

  const { mph, handicap, points, sizeClass, error } = useMemo(() => {
    const t = parseFloat(time);
    const h = parseFloat(height);
    if (!t || t <= 0) return { mph: 0, handicap: 0, points: 0, sizeClass: "", error: "" };
    if (t > 60) return { mph: 0, handicap: 0, points: 0, sizeClass: "", error: "Time looks too high — Fast CAT runs are under 60 seconds." };
    const mphCalc = calculateSpeed(t);
    if (!h || h <= 0) return { mph: mphCalc, handicap: 0, points: 0, sizeClass: "(enter height for points)", error: "" };
    const hc = calculateHandicap(h);
    const pts = calculatePoints(mphCalc, hc);
    const sz = hc === 2.0 ? 'Below 12"' : hc === 1.5 ? '12"–18"' : '18" and up';
    return { mph: mphCalc, handicap: hc, points: pts, sizeClass: sz, error: "" };
  }, [time, height]);

  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardEmoji}>⏱️</Text>
        <View style={{ flex: 1 }}>
          <Text style={styles.cardTitle}>Points & MPH Calculator</Text>
          <Text style={styles.cardHint}>Time + height → MPH, handicap, and points earned</Text>
        </View>
      </View>

      <Text style={styles.label}>Run Time (seconds)</Text>
      <TextInput
        value={time}
        onChangeText={setTime}
        keyboardType="decimal-pad"
        placeholder="e.g. 7.82"
        placeholderTextColor="#9CA3AF"
        style={styles.input}
      />

      <Text style={styles.label}>Dog Height at Withers (inches)</Text>
      <TextInput
        value={height}
        onChangeText={setHeight}
        keyboardType="decimal-pad"
        placeholder="e.g. 18"
        placeholderTextColor="#9CA3AF"
        style={styles.input}
      />

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      <View style={styles.resultGrid}>
        <ResultBox label="MPH" value={mph ? mph.toFixed(2) : "—"} />
        <ResultBox label="Handicap" value={handicap ? `${handicap.toFixed(1)}×` : "—"} sub={sizeClass} />
        <ResultBox label="Points" value={points ? points.toFixed(2) : "—"} highlight />
      </View>
    </View>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Tool 2: Title Progress
// Inputs: current points + avg points per run → runs to next title
// ──────────────────────────────────────────────────────────────────────────────
function TitleProgressCalculator() {
  const [current, setCurrent] = useState("");
  const [avg, setAvg] = useState("");

  const { nextTitle, runsNeeded, pointsToGo, error } = useMemo(() => {
    const c = parseFloat(current);
    const a = parseFloat(avg);
    if (isNaN(c) || c < 0) return { nextTitle: null as any, runsNeeded: 0, pointsToGo: 0, error: "" };
    if (!a || a <= 0) return { nextTitle: nextTitleFor(c), runsNeeded: 0, pointsToGo: 0, error: "" };
    const t = nextTitleFor(c);
    if (!t) return { nextTitle: null as any, runsNeeded: 0, pointsToGo: 0, error: "" };
    const togo = Math.max(0, t.threshold - c);
    const runs = Math.ceil(togo / a);
    return { nextTitle: t, runsNeeded: runs, pointsToGo: togo, error: "" };
  }, [current, avg]);

  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardEmoji}>🏆</Text>
        <View style={{ flex: 1 }}>
          <Text style={styles.cardTitle}>Title Progress Calculator</Text>
          <Text style={styles.cardHint}>How many runs until the next AKC title?</Text>
        </View>
      </View>

      <Text style={styles.label}>Current Career Points</Text>
      <TextInput
        value={current}
        onChangeText={setCurrent}
        keyboardType="decimal-pad"
        placeholder="e.g. 425"
        placeholderTextColor="#9CA3AF"
        style={styles.input}
      />

      <Text style={styles.label}>Average Points Per Run</Text>
      <TextInput
        value={avg}
        onChangeText={setAvg}
        keyboardType="decimal-pad"
        placeholder="e.g. 32"
        placeholderTextColor="#9CA3AF"
        style={styles.input}
      />

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      <View style={styles.resultGrid}>
        <ResultBox label="Next Title" value={nextTitle ? nextTitle.name : "—"} sub={nextTitle ? `${nextTitle.threshold} pts` : ""} />
        <ResultBox label="Points to Go" value={pointsToGo ? pointsToGo.toFixed(0) : "—"} />
        <ResultBox label="Runs Needed" value={runsNeeded ? String(runsNeeded) : "—"} highlight />
      </View>
    </View>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Tool 3: Run #3 Target Calculator
// Inputs: Time 1 + Time 2 + target avg MPH → required Run #3 time
// ──────────────────────────────────────────────────────────────────────────────
function Run3TargetCalculator() {
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [target, setTarget] = useState("");

  // Inline calc on every render. Target is always MPH (the time-mode toggle
  // was rolled back — confusing UX + tripped a TDZ bug in the prod bundle).
  var KMPH = (100 * 3600) / 1760;
  var mph1 = 0, mph2 = 0, curAvgMph = 0, curAvgTime = 0;
  var neededMph = 0, neededTime = 0, achievable = true, error = "";
  var a = parseFloat(t1), b = parseFloat(t2), tgt = parseFloat(target);
  if (a > 0 && b > 0) {
    mph1 = KMPH / a;
    mph2 = KMPH / b;
    curAvgMph = (mph1 + mph2) / 2;
    curAvgTime = (a + b) / 2;
    if (tgt > 0) {
      var needMph = 3 * tgt - mph1 - mph2;
      if (needMph <= 0) { achievable = false; error = "Already past target — even a no-show on run 3 would average above " + tgt.toFixed(2) + " MPH."; }
      else if (needMph > 45) { neededMph = needMph; neededTime = KMPH / needMph; achievable = false; error = "Run 3 would need " + needMph.toFixed(2) + " MPH — faster than any recorded Fast CAT run. Pick a more realistic target."; }
      else { neededMph = needMph; neededTime = KMPH / needMph; achievable = true; }
    }
  }

  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardEmoji}>🎯</Text>
        <View style={{ flex: 1 }}>
          <Text style={styles.cardTitle}>Run #3 Target Calculator</Text>
          <Text style={styles.cardHint}>Two times in the books — what does run #3 need to be to hit your target 3-run average?</Text>
        </View>
      </View>

      <Text style={styles.label}>Run 1 Time (seconds)</Text>
      <TextInput
        value={t1}
        onChangeText={setT1}
        keyboardType="decimal-pad"
        placeholder="e.g. 7.62"
        placeholderTextColor="#9CA3AF"
        style={styles.input}
      />

      <Text style={styles.label}>Run 2 Time (seconds)</Text>
      <TextInput
        value={t2}
        onChangeText={setT2}
        keyboardType="decimal-pad"
        placeholder="e.g. 7.84"
        placeholderTextColor="#9CA3AF"
        style={styles.input}
      />

      <Text style={styles.label}>Target 3-Run Average (MPH)</Text>
      <TextInput
        value={target}
        onChangeText={setTarget}
        keyboardType="decimal-pad"
        placeholder="e.g. 27.00"
        placeholderTextColor="#9CA3AF"
        style={styles.input}
      />

      {(mph1 > 0 && mph2 > 0) ? (
        <View style={styles.infoRow}>
          <Text style={styles.infoText}>Current 2-run average: <Text style={styles.infoStrong}>{curAvgMph.toFixed(2)} MPH · {curAvgTime.toFixed(3)}s</Text></Text>
          <Text style={styles.infoText}>Run 1: {mph1.toFixed(2)} mph · Run 2: {mph2.toFixed(2)} mph</Text>
          {(achievable && neededTime > 0 && neededMph > 0) ? (
            <Text style={[styles.infoText, { marginTop: 4 }]}>Run 3 needed: <Text style={styles.infoStrong}>{neededMph.toFixed(2)} mph · {neededTime.toFixed(3)}s</Text></Text>
          ) : null}
        </View>
      ) : null}

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      {(achievable && neededTime > 0 && neededMph > 0) ? (
        <View style={styles.projRow}>
          <Text style={styles.projLabel}>Projected Best-of-3 Average</Text>
          <Text style={styles.projValue}>
            {((mph1 + mph2 + neededMph) / 3).toFixed(2)} MPH
            <Text style={styles.projSep}>  ·  </Text>
            {((parseFloat(t1) + parseFloat(t2) + neededTime) / 3).toFixed(3)}s
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function ResultBox({ label, value, sub, highlight }: { label: string; value: string; sub?: string; highlight?: boolean }) {
  return (
    <View style={[styles.resultBox, highlight && styles.resultBoxHighlight]}>
      <Text style={[styles.resultLabel, highlight && { color: ORANGE }]}>{label}</Text>
      <Text style={[styles.resultValue, highlight && { color: NAVY }]}>{value}</Text>
      {sub ? <Text style={styles.resultSub}>{sub}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    backgroundColor: NAVY,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    paddingTop: Platform.OS === "ios" ? 50 : 14,
  },
  backButton: { width: 60 },
  backText: { color: "rgba(255,255,255,0.8)", fontSize: 15, fontWeight: "500" },
  headerTitle: { color: WHITE, fontSize: 16, fontWeight: "700" },
  content: { flexGrow: 1 },
  hero: {
    backgroundColor: "#FFF7E6",
    borderBottomWidth: 1,
    borderBottomColor: "#F5DEB3",
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 40,
    alignItems: "center",
  },
  heroTitle: { color: NAVY, fontSize: 32, fontWeight: "900", textAlign: "center" },
  heroSubtitle: { color: "#92400E", fontSize: 15, marginTop: 8, textAlign: "center" },
  body: {
    maxWidth: 720,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: 20,
    paddingVertical: 28,
    gap: 22,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 8,
    elevation: 2,
  },
  cardHead: { flexDirection: "row", alignItems: "flex-start", gap: 12, marginBottom: 14 },
  cardEmoji: { fontSize: 26 },
  cardTitle: { fontSize: 18, fontWeight: "800", color: NAVY },
  cardHint: { fontSize: 13, color: "#6B7280", marginTop: 2, lineHeight: 18 },
  label: { fontSize: 13, fontWeight: "600", color: "#374151", marginTop: 10, marginBottom: 4 },
  input: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: "#111827",
    backgroundColor: "#FFFFFF",
  },
  infoRow: { marginTop: 12, padding: 10, backgroundColor: "#F9FAFB", borderRadius: 8, borderWidth: 1, borderColor: "#F3F4F6" },
  infoText: { fontSize: 13, color: "#4B5563", lineHeight: 20 },
  infoStrong: { color: NAVY, fontWeight: "700" },
  errorText: { color: "#B91C1C", fontSize: 13, marginTop: 10, lineHeight: 18 },
  resultGrid: { flexDirection: "row", flexWrap: "wrap", gap: 10, marginTop: 16 },
  resultBox: {
    flexBasis: 0,
    flexGrow: 1,
    minWidth: 110,
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    padding: 12,
    alignItems: "center",
  },
  resultBoxHighlight: { backgroundColor: "#FEF3C7", borderWidth: 1, borderColor: ORANGE },
  resultLabel: { fontSize: 11, fontWeight: "700", color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 },
  resultValue: { fontSize: 22, fontWeight: "900", color: NAVY, marginTop: 6 },
  resultSub: { fontSize: 11, color: "#6B7280", marginTop: 2 },
  toggleGroup: { flexDirection: "row", backgroundColor: "#F3F4F6", borderRadius: 8, padding: 2, borderWidth: 1, borderColor: "#E5E7EB" },
  toggleBtn: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 6 },
  toggleBtnActive: { backgroundColor: NAVY },
  toggleText: { fontSize: 12, fontWeight: "700", color: "#6B7280" },
  toggleTextActive: { color: WHITE },
  projRow: { marginTop: 14, padding: 14, backgroundColor: NAVY, borderRadius: 10, alignItems: "center" },
  projLabel: { color: ORANGE, fontSize: 11, fontWeight: "700", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 },
  projValue: { color: WHITE, fontSize: 18, fontWeight: "800" },
  projSep: { color: "rgba(255,255,255,0.4)" },
  foot: { fontSize: 12, color: "#9CA3AF", textAlign: "center", marginTop: 10, paddingHorizontal: 8, lineHeight: 18 },
  footer: { backgroundColor: NAVY_DARK, paddingVertical: 32, alignItems: "center" },
  footerText: { color: "rgba(255,255,255,0.35)", fontSize: 13 },
});
